import PictureComponent from "/components/core/picture/picture-component";
import Carousel from "segments/desktop/core-components/carousel/carousel";
import DefaultProps from "./default-props";
import { componentStyles } from "./carousel-asset-component.scss";
import { useCallback, useMemo, useRef } from "react";
import { trackEvent } from "services/bigbrain-service";
import { CAROUSEL_USER_SWIPE } from "constants/bigbrain-event-types";
import classnames from "classnames";

const CarouselAssetComponent = ({
  autoplay,
  imageHeight,
  horizontalPadding,
  slides,
  centerPadding,
  disableCenterModeStyle,
  pauseOnHover,
  transitionSpeed,
  ...rest
}) => {
  const assets = useMemo(() => (slides || []).map((slide) => slide?.asset?.image), [slides]);
  const isUserSlideRef = useRef(null);
  const slider = useRef(null);
  const currentIndex = useRef(0);

  const pauseCarouselOnEvent = useCallback(
    () => pauseOnHover && autoplay && slider.current?.slickPause(),
    [autoplay, pauseOnHover]
  );

  const onItemClick = useCallback(
    (event, slideIndex) => {
      if (slideIndex !== currentIndex.current) {
        isUserSlideRef.current = true;
      }

      if (slideIndex === 0 && currentIndex.current === assets.length - 1) {
        slider.current?.slickNext();
        event.stopPropagation();
      }

      if (slideIndex === assets.length - 1 && currentIndex.current === 0) {
        slider.current.slickPrev();
        event.stopPropagation();
      }
    },
    [assets]
  );

  const itemRenderer = useCallback(
    (image, index) => (
      <div onClick={(event) => onItemClick(event, index)}>
        <PictureComponent key={image?.src} className="image-slide" src={image?.src} alt={image?.alt} />
      </div>
    ),
    [onItemClick]
  );

  const onSlideSwipe = useCallback(() => {
    isUserSlideRef.current = true;
    pauseCarouselOnEvent();
  }, [pauseCarouselOnEvent]);

  const onSlideChange = useCallback(
    (index) => {
      if (isUserSlideRef.current) {
        trackEvent(CAROUSEL_USER_SWIPE, { kind: assets[index]?.src, info1: index });
        isUserSlideRef.current = false;
      }
    },
    [assets]
  );

  const carouselProps = useMemo(
    () => ({
      items: assets,
      autoplay,
      beforeChange: (oldIndex, newIndex) => {
        currentIndex.current = newIndex;
      },
      onRefSet: (ref) => {
        slider.current = ref;
      },
      pauseOnHover,
      itemRenderer,
      speed: transitionSpeed,
      defaultIndex: 0,
      onChange: onSlideChange,
      onSwipe: onSlideSwipe,
      slidesToShow: 1,
      slidesToScroll: 1,
      centerPadding: centerPadding || undefined,
      swipeToSlide: true
    }),
    [assets, autoplay, centerPadding, itemRenderer, onSlideChange, onSlideSwipe, pauseOnHover, transitionSpeed]
  );

  return (
    <div
      onMouseEnter={pauseCarouselOnEvent}
      onMouseLeave={() => pauseOnHover && autoplay && slider.current?.slickPlay()}
      className={classnames("carousel-asset-component", { "default-style": disableCenterModeStyle })}
      style={{
        "--image-height": imageHeight,
        "--image-padding": horizontalPadding
      }}
    >
      <Carousel {...rest} {...carouselProps} />

      <style jsx>{componentStyles}</style>
    </div>
  );
};

CarouselAssetComponent.defaultProps = DefaultProps;

export default CarouselAssetComponent;
